import React, { useState } from 'react';
import { Customer } from '../../types';
import { useViewStore } from '../../store/viewStore';
import { usePoleStore } from '../../stores/poleStore';
import { useCustomerStore } from '../../stores/customerStore';
import { useAuthStore } from '../../stores/authStore';
import './CustomerDialogs.css';

interface CustomerDetailDialogProps {
  customer: Customer;
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  onEdit?: () => void;
  onDelete?: (customerId: number) => void;
  onTerminate?: (customerId: number) => void;
}

const CustomerDetailDialog: React.FC<CustomerDetailDialogProps> = ({
  customer,
  open,
  onClose,
  onRefresh,
  onEdit,
  onDelete,
  onTerminate
}) => {
  const { setViewMode } = useViewStore();
  const { poles, setSelectedPole, setZoomTarget } = usePoleStore();
  const { token } = useAuthStore();
  const [showInstallationDialog, setShowInstallationDialog] = useState(false);

  if (!open) return null;

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: 'Aktívny',
      suspended: 'Pozastavený',
      terminated: 'Ukončený',
      pending_installation: 'Čaká na inštaláciu',
    };
    return labels[status] || status;
  };

  const getServiceTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      internet: 'Internet',
      internet_tv: 'Internet + TV',
      internet_tv_phone: 'Internet + TV + Telefón',
    };
    return labels[type] || type;
  };

  const handlePoleClick = () => {
    if (!customer.pole_id) return;

    // Find the pole
    const pole = poles.find(p => p.id === customer.pole_id);
    if (!pole) {
      alert('Stĺpik nebol nájdený');
      return;
    }

    // Get pole coordinates
    const lat = typeof pole.latitude === 'string' ? parseFloat(pole.latitude) : pole.latitude;
    const lng = typeof pole.longitude === 'string' ? parseFloat(pole.longitude) : pole.longitude;

    if (!lat || !lng) {
      alert('Stĺpik nemá GPS súradnice');
      return;
    }

    // Set zoom target to pole location with high zoom level
    setZoomTarget({ lat, lng, zoom: 18 });

    // Switch to map view and select the pole
    setViewMode('map');
    setSelectedPole(pole);
    onClose();
  };

  const handleReturnToPending = async () => {
    if (!token) return;

    const confirmed = window.confirm(
      'Naozaj chcete vrátiť zákazníka do stavu "Čaká na inštaláciu"?\n\n' +
      'Toto vymaže dátum inštalácie a poznámky.'
    );

    if (!confirmed) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== '' ? process.env.REACT_APP_API_URL : '/api';
      const response = await fetch(
        `${API_URL}/customers/${customer.id}/return-to-pending`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();

        // Check if customer is connected to splitter
        if (error.detail?.customer_connected) {
          const pole = poles.find(p => p.id === error.detail.pole_id);

          if (pole) {
            const goToPole = window.confirm(
              `❌ Zákazník je pripojený na splitter!\n\n` +
              `Stĺpik: ${error.detail.pole_name}\n\n` +
              `Najprv odpojte zákazníka od splitteru.\n\n` +
              `Chcete prejsť na stĺpik a otvoriť montážny checklist?`
            );

            if (goToPole) {
              // Get pole coordinates
              const lat = typeof pole.latitude === 'string' ? parseFloat(pole.latitude) : pole.latitude;
              const lng = typeof pole.longitude === 'string' ? parseFloat(pole.longitude) : pole.longitude;

              if (lat && lng) {
                // Zoom to pole
                setZoomTarget({ lat, lng, zoom: 18 });
                setViewMode('map');
                setSelectedPole(pole);

                // Show installation dialog after a short delay
                setTimeout(() => {
                  setShowInstallationDialog(true);
                }, 500);

                onClose();
              }
            }
          } else {
            alert(`❌ ${error.detail.message}\n\nStĺpik: ${error.detail.pole_name} (ID: ${error.detail.pole_id})`);
          }
        } else {
          throw new Error(error.detail || 'Zákazníka sa nepodarilo vrátiť medzi čakajúcich');
        }
        return;
      }

      alert('✅ Zákazník bol vrátený do stavu "Čaká na inštaláciu"');

      // Refresh customer list
      if (onRefresh) {
        onRefresh();
      }

      onClose();
    } catch (error: any) {
      console.error('Error returning customer to pending:', error);
      alert(`❌ Chyba: ${error.message}`);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog-container">
        <div className="dialog-header">
          <h2>Detail zákazníka</h2>
          <button onClick={onClose} className="dialog-close">
            ×
          </button>
        </div>

        <div className="dialog-content">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Basic Info */}
            <div className="detail-section">
              <h3>Základné informácie</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Číslo zákazníka</div>
                  <div className="detail-value">{customer.customer_number}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Stav</div>
                  <div className="detail-value">{getStatusLabel(customer.status)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Typ zákazníka</div>
                  <div className="detail-value">
                    {customer.customer_type === 'individual' ? 'Fyzická osoba' : 'Firma'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Dátum vytvorenia</div>
                  <div className="detail-value">
                    {new Date(customer.created_at).toLocaleDateString('sk-SK')}
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="detail-section">
              <h3>
                {customer.customer_type === 'individual' ? 'Osobné údaje' : 'Firemné údaje'}
              </h3>
              <div className="detail-grid">
                {customer.customer_type === 'individual' ? (
                  <>
                    <div className="detail-item">
                      <div className="detail-label">Meno</div>
                      <div className="detail-value">{customer.first_name}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">Priezvisko</div>
                      <div className="detail-value">{customer.last_name}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="detail-item full-width">
                      <div className="detail-label">Názov firmy</div>
                      <div className="detail-value">{customer.company_name}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">IČO</div>
                      <div className="detail-value">{customer.ico || '-'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-label">DIČ</div>
                      <div className="detail-value">{customer.dic || '-'}</div>
                    </div>
                    {customer.dic_dph && (
                      <div className="detail-item full-width">
                        <div className="detail-label">IČ DPH</div>
                        <div className="detail-value">{customer.dic_dph}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="detail-section">
              <h3>Adresa</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <div className="detail-label">Ulica a číslo</div>
                  <div className="detail-value">{customer.address}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Mesto</div>
                  <div className="detail-value">{customer.city}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">PSČ</div>
                  <div className="detail-value">{customer.zip_code}</div>
                </div>
                {(customer.latitude && customer.longitude) && (
                  <div className="detail-item full-width">
                    <div className="detail-label">GPS súradnice</div>
                    <div className="detail-value">
                      {customer.latitude}, {customer.longitude}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div className="detail-section">
              <h3>Kontakt</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{customer.email}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Telefón</div>
                  <div className="detail-value">{customer.phone}</div>
                </div>
              </div>
            </div>

            {/* Service */}
            <div className="detail-section">
              <h3>Služba</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Typ služby</div>
                  <div className="detail-value">{getServiceTypeLabel(customer.service_type)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Rýchlosť</div>
                  <div className="detail-value">{customer.service_speed || '-'}</div>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div className="detail-section">
              <h3>Technické údaje</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">ONT Serial</div>
                  <div className="detail-value">{customer.ont_serial || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">ONT MAC</div>
                  <div className="detail-value">{customer.ont_mac || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">VLAN ID</div>
                  <div className="detail-value">{customer.vlan_id || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">IP adresa</div>
                  <div className="detail-value">{customer.ip_address || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Port</div>
                  <div className="detail-value">{customer.port_number || '-'}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Stĺpik</div>
                  <div className="detail-value">
                    {customer.pole_name ? (
                      <span
                        onClick={handlePoleClick}
                        style={{
                          color: '#4CAF50',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        title="Kliknite pre zobrazenie na mape"
                      >
                        {customer.pole_name}
                      </span>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Installation */}
            {customer.installation_date && (
              <div className="detail-section">
                <h3>Inštalácia</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-label">Dátum inštalácie</div>
                    <div className="detail-value">
                      {new Date(customer.installation_date).toLocaleDateString('sk-SK')}
                    </div>
                  </div>
                  {customer.installation_notes && (
                    <div className="detail-item full-width">
                      <div className="detail-label">Poznámky k inštalácii</div>
                      <div className="detail-value">{customer.installation_notes}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing */}
            <div className="detail-section">
              <h3>Fakturácia</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Mesačný poplatok</div>
                  <div className="detail-value">
                    {customer.monthly_fee ? `${customer.monthly_fee} €` : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Deň fakturácie</div>
                  <div className="detail-value">
                    {customer.billing_day ? `${customer.billing_day}. deň v mesiaci` : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {(customer.customer_notes || customer.internal_notes) && (
              <div className="detail-section">
                <h3>Poznámky</h3>
                {customer.customer_notes && (
                  <div style={{ marginBottom: '12px' }}>
                    <div className="detail-label">Poznámky zákazníka</div>
                    <div className="detail-value">{customer.customer_notes}</div>
                  </div>
                )}
                {customer.internal_notes && (
                  <div>
                    <div className="detail-label">Interné poznámky</div>
                    <div className="detail-value">{customer.internal_notes}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="dialog-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {/* Left side - Danger actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {/* Show "Return to Pending" button only if customer is not already pending */}
            {customer.status !== 'pending_installation' && (
              <button
                onClick={handleReturnToPending}
                className="btn btn-warning"
                style={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                }}
              >
                🔄 Vrátiť do čakajúcich
              </button>
            )}

            {/* Terminate service button - only for active customers */}
            {onTerminate && customer.status === 'active' && (
              <button
                onClick={() => {
                  onTerminate(customer.id);
                  onClose();
                }}
                className="btn btn-danger"
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                }}
                title="Ukončiť službu zákazníka"
              >
                🚫 Ukončiť službu
              </button>
            )}

            {/* Delete customer button */}
            {onDelete && (
              <button
                onClick={() => {
                  onDelete(customer.id);
                  onClose();
                }}
                className="btn btn-danger"
                style={{
                  backgroundColor: '#991b1b',
                  color: 'white',
                }}
                title="Vymazať zákazníka z databázy"
              >
                🗑️ Vymazať
              </button>
            )}
          </div>

          {/* Right side - Normal actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {onEdit && (
              <button
                onClick={() => {
                  onEdit();
                  onClose();
                }}
                className="btn btn-primary"
                style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                }}
              >
                ✏️ Upraviť
              </button>
            )}
            <button onClick={onClose} className="btn btn-secondary">
              Zavrieť
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailDialog;

